import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Pagination from '@/components/ui/Pagination';
import type { Marker } from '@/types';
import api from '@/services/api';

const MarkersList: React.FC = () => {
  const [markers, setMarkers] = useState<Marker[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrev: false
  });

  // Filtri
  const [filters, setFilters] = useState({
    search: '',
    city: '',
    province: '',
    region: '',
    postalCode: '',
    technology: '',
    reportStatus: '',
    verified: '',
    dateFrom: '',
    dateTo: ''
  });

  const [showFilters, setShowFilters] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);

  // Hook per debounce dei filtri
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  // Debounce filters per evitare troppe chiamate API
  useEffect(() => {
    // Se i filtri sono diversi da quelli debounced, mostra indicatore di caricamento
    const hasChanges = JSON.stringify(filters) !== JSON.stringify(debouncedFilters);
    setIsFiltering(hasChanges);

    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500); // 500ms di ritardo

    return () => clearTimeout(timer);
  }, [filters, debouncedFilters]);

  // Fetch markers solo quando cambiano i filtri debounced o la pagina
  useEffect(() => {
    fetchMarkers();
  }, [pagination.currentPage, debouncedFilters]);

  const fetchMarkers = async () => {
    try {
      setLoading(true);

      // Costruisci query params
      const queryParams = new URLSearchParams({
        page: pagination.currentPage.toString(),
        limit: '10'
      });

      // Aggiungi filtri se non vuoti
      Object.entries(debouncedFilters).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, value);
        }
      });

      const response = await api.get(`/markers?${queryParams.toString()}`);

      if (response.data.success) {
        setMarkers(response.data.data);
        const paginationData = response.data.pagination;
        setPagination({
          currentPage: paginationData?.current || 1,
          totalPages: paginationData?.total || 1,
          totalItems: paginationData?.totalRecords || 0,
          hasNext: (paginationData?.current || 1) < (paginationData?.total || 1),
          hasPrev: (paginationData?.current || 1) > 1
        });
      }
    } catch (error) {
      console.error('Error fetching markers:', error);
    } finally {
      setLoading(false);
      setIsFiltering(false);
    }
  };

  const handleDeleteMarker = async (markerId: string) => {
    if (!confirm('Sei sicuro di voler eliminare questa segnalazione?')) {
      return;
    }

    try {
      await api.delete(`/markers/${markerId}`);
      setMarkers(prev => prev.filter(m => m._id !== markerId));
    } catch (error) {
      console.error('Error deleting marker:', error);
    }
  };

  const handleVerifyMarker = async (markerId: string) => {
    try {
      const response = await api.post(`/markers/${markerId}/verify`);

      if (response.data.success) {
        setMarkers(prev => prev.map(m =>
          m._id === markerId ? { ...m, metadata: { ...m.metadata, verified: true } } : m
        ));
      }
    } catch (error) {
      console.error('Error verifying marker:', error);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      city: '',
      province: '',
      region: '',
      postalCode: '',
      technology: '',
      reportStatus: '',
      verified: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Gestione Segnalazioni
            </h1>
            <p className="text-gray-600">
              Visualizza e gestisci tutte le segnalazioni
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri'}
            </button>
            <Link
              to="/admin/markers/new"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Nuova Segnalazione
            </Link>
          </div>
        </div>

        {/* Sezione Filtri */}
        {showFilters && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-900">Filtri</h2>
                {isFiltering && (
                  <div className="flex items-center gap-1 text-blue-600 text-sm">
                    <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                    Filtraggio...
                  </div>
                )}
              </div>
              <button
                onClick={clearFilters}
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                Pulisci tutti
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ricerca</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Via, città, CAP..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Città</label>
                <input
                  type="text"
                  value={filters.city}
                  onChange={(e) => handleFilterChange('city', e.target.value)}
                  placeholder="es. Roma"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Provincia</label>
                <input
                  type="text"
                  value={filters.province}
                  onChange={(e) => handleFilterChange('province', e.target.value)}
                  placeholder="es. RM"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CAP</label>
                <input
                  type="text"
                  value={filters.postalCode}
                  onChange={(e) => handleFilterChange('postalCode', e.target.value)}
                  placeholder="es. 00100"
                  maxLength={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Regione</label>
                <input
                  type="text"
                  value={filters.region}
                  onChange={(e) => handleFilterChange('region', e.target.value)}
                  placeholder="es. Lazio"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tecnologia</label>
                <select
                  value={filters.technology}
                  onChange={(e) => handleFilterChange('technology', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Tutte</option>
                  <option value="ADSL">ADSL</option>
                  <option value="VDSL">VDSL</option>
                  <option value="FTTC">FTTC</option>
                  <option value="FTTH">FTTH</option>
                  <option value="FWA">FWA</option>
                  <option value="Satellite">Satellite</option>
                  <option value="Mobile">Mobile</option>
                  <option value="None">Nessuna</option>
                  <option value="Unknown">Non specificato</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stato Segnalazione</label>
                <select
                  value={filters.reportStatus}
                  onChange={(e) => handleFilterChange('reportStatus', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Tutti</option>
                  <option value="open">Aperto</option>
                  <option value="in_progress">In corso</option>
                  <option value="resolved">Risolto</option>
                  <option value="closed">Chiuso</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Verificato</label>
                <select
                  value={filters.verified}
                  onChange={(e) => handleFilterChange('verified', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Tutti</option>
                  <option value="true">Verificato</option>
                  <option value="false">Da verificare</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data da</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data a</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Posizione
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tecnologia
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Segnalazioni
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stato
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {markers.map((marker) => (
                  <tr key={marker._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {marker.streetName}
                        </div>
                        {marker.streetNameCorrection && (
                          <div className="text-sm text-gray-500">
                            Corretto: {marker.streetNameCorrection}
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {marker.currentLine.technology}
                      </div>
                      {marker.currentLine.operator && (
                        <div className="text-sm text-gray-500">
                          {marker.currentLine.operator}
                        </div>
                      )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {marker.reports.length} report{marker.reports.length !== 1 ? 's' : ''}
                      </div>
                      {marker.reports.length > 0 && (
                        <div className="text-xs text-gray-500">
                          {marker.reports.filter(r => r.status === 'open').length} aperti
                        </div>
                      )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${marker.metadata.verified
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                        }`}>
                        {marker.metadata.verified ? 'Verificato' : 'Da Verificare'}
                      </span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(marker.createdAt).toLocaleDateString('it-IT')}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <Link
                        to={`/admin/markers/${marker._id}`}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        Visualizza
                      </Link>

                      <Link
                        to={`/admin/markers/edit/${marker._id}`}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Modifica
                      </Link>

                      {!marker.metadata.verified && (
                        <button
                          onClick={() => handleVerifyMarker(marker._id)}
                          className="text-green-600 hover:text-green-900"
                        >
                          Verifica
                        </button>
                      )}

                      <button
                        onClick={() => handleDeleteMarker(marker._id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Elimina
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            pagination={pagination}
            onPageChange={(newPage) => setPagination(prev => ({ ...prev, currentPage: newPage }))}
            itemName="segnalazioni"
            showItemCount={true}
          />
        </div>
      </div>
    </div>
  );
};

export default MarkersList;